import type { Attachment, DataExport } from "../../domain/entities";
import { validateAttachmentContent, validateAttachmentFile } from "../../domain/attachments";

export function inspectAttachmentImport(data: DataExport) {
  const errors: string[] = [];
  const files: Array<{ attachment: Attachment; blob: Blob }> = [];
  const totals = new Map<string, { count: number; bytes: number }>();
  for (const attachment of data.attachments ?? []) {
    if (attachment.state !== "ready") continue;
    try {
      const content = data.attachmentContents?.[attachment.id];
      if (!content) throw new Error("file content is missing");
      const bytes = Uint8Array.from(atob(content), (character) => character.charCodeAt(0));
      const problem = validateAttachmentFile({ name: attachment.originalName, type: attachment.mediaType, size: bytes.length })
        ?? validateAttachmentContent(attachment.originalName, attachment.mediaType, bytes);
      if (problem) throw new Error(problem);
      if (bytes.length !== attachment.byteSize) throw new Error("file size does not match");
      if (!data.tasks.some((task) => task.id === attachment.taskId)) throw new Error("owning Task is missing");
      if (attachment.source !== "task" && attachment.source !== "comment") throw new Error("source is invalid");
      if (attachment.source === "comment" && !data.comments?.some((comment) => comment.id === attachment.commentId && comment.taskId === attachment.taskId)) throw new Error("owning Comment is missing");
      const total = totals.get(attachment.taskId) ?? { count: 0, bytes: 0 };
      total.count++; total.bytes += bytes.length; totals.set(attachment.taskId, total);
      if (total.count > 20 || total.bytes > 200 * 1024 * 1024) throw new Error("Task attachment limit is exceeded");
      files.push({ attachment, blob: new Blob([bytes], { type: attachment.mediaType }) });
    } catch (error) { errors.push(`Attachment ${attachment.originalName}: ${error instanceof Error ? error.message : "invalid file"}.`); }
  }
  return { errors, files };
}

export async function attachmentBase64(blob: Blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}
